
import React from 'react';
import Modal from 'sushiweb/Modal';
import Button from 'sushiweb/Button';
import ZImage from 'sushiweb/ZImage';
import CounterInput from '../../../components/shared/counterInput';
import Spinner from 'sushiweb/Spinner';
import { newLineText, getFormattedINRValues } from '../../../utils/helpers';
import styles from './ProductOffersModal.module.scss';

const convertToSet = (arr) => {
  if (!arr) return new Set();
  if (arr instanceof Set) return arr;
  return new Set(Array.isArray(arr) ? arr : [arr]);
};

class ProductOffersModal extends React.Component {
  constructor(props) {
    super(props);
    const { product } = props;
    this.state = {
      currentQuantity: parseInt(product?.Quantity?.CartQuantity) || 0,
    };
  }

  componentDidUpdate(prevProps) {
    const { product } = this.props;
    const prevProduct = prevProps.product;
    
    // Update local state when cart quantity changes
    if (prevProduct?.Quantity?.CartQuantity !== product?.Quantity?.CartQuantity) {
      this.setState({
        currentQuantity: parseInt(product?.Quantity?.CartQuantity) || 0,
      });
    }
  }

  handleAddClick = (quantity, inputType) => {
    const { onAddToCart } = this.props;
    onAddToCart(quantity, inputType);
    // Optimistically update the quantity state
    this.setState({ currentQuantity: quantity });
  };

  handleQuantityChange = (value, inputType) => {
    const { onAddToCart } = this.props;
    onAddToCart(value, inputType);
    // Optimistically update the quantity state
    this.setState({ currentQuantity: value });
  };

  isLoading = () => {
    const { product, inProgressCart } = this.props;
    if (!inProgressCart || !product) return false;
    
    // Safely check if cart operations are in progress
    const addInProgress = inProgressCart.add ? convertToSet(inProgressCart.add).has(product.Id) : false;
    const updateInProgress = inProgressCart.update ? convertToSet(inProgressCart.update).has(product.Id) : false;
    
    return addInProgress || updateInProgress;
  };

  renderProductHeaderSection = () => {
    const { product, isAuthenticated, onSignInClick } = this.props;
    if (!product) return null;

    const minOrderQty = parseInt(product.Quantity?.MinimumOrderQuantity) || 1;

    return (
      <div className={styles.productHeaderSection}>
        {/* Product Info Row */}
        <div className={styles.productInfoRow}>
          <div className={styles.productImageContainer}>
            <ZImage
              src={product.ImagePath}
              alt={product.Name}
              className={styles.productImage}
            />
          </div>
          <div className={styles.productDetails}>
            <h3 className={styles.productName}>{product.Name}</h3>
            <div className={styles.productMeta}>
              {product?.Quantity?.DisplayValue && (
                <span className={styles.productWeight}>
                  {product.Quantity.DisplayValue}
                </span>
              )}
              {product?.Rating?.rating > 0 && (
                <>
                  <span className={styles.metaSeparator}>|</span>
                  <div className={styles.ratingSection}>
                    <img
                      src="https://assets.hyperpure.com/data/images/starIcon/starIcon1759252824.png"
                      alt="Star icon"
                      width="14"
                      height="14"
                      className={styles.starIcon}
                    />
                    <span className={styles.ratingValue}>
                      {product.Rating.rating}
                    </span>
                    <span className={styles.ratingCount}>
                      {product?.Rating?.ratings_count_text}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Price and ADD Button Row */}
        <div className={styles.priceAddRow}>
          <div className={styles.pricingSection}>
            <div className={styles.priceRow}>
              <span
                className={styles.mainPrice}
                style={{ color: product?.UnitPriceV2?.Color }}
              >
                {product?.UnitPriceV2?.Price}
              </span>
              {product?.UnitPriceV2?.CompareAtPrice && (
                <span className={styles.strikePrice}>
                  {product.UnitPriceV2.CompareAtPrice}
                </span>
              )}
            </div>
            {product?.UnitPriceV2?.SubHeading && (
              <div className={styles.priceSubHeading}>
                {product.UnitPriceV2.SubHeading}
              </div>
            )}
          </div>

          {/* Show counter input if item is in cart, otherwise show ADD button */}
          {this.state.currentQuantity > 0 ? (
            <div className={styles.counterWrapper}>
              <CounterInput
                type="text"
                placeholder={0}
                quantityChangeHandler={(val) =>
                  this.handleQuantityChange(val, product)
                }
                minLimit={0}
                maxLimit={undefined}
                defaultVal={this.state.currentQuantity}
                size="small"
                showLoader={this.isLoading()}
                minOrderQty={minOrderQty}
              />
            </div>
          ) : (
            <div className={styles.addButtonContainer}>
              <Button
                type="submit"
                loading={this.isLoading()}
                size="small"
                btnColor="red"
                iconAlign="right"
                appearance="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isAuthenticated) {
                    onSignInClick && onSignInClick();
                  } else {
                    this.handleAddClick(minOrderQty, null);
                  }
                }}
                className={`${styles.addButtonSushi} ${minOrderQty > 1 ? styles.addButtonWithMinQty : ''}`}
                disabled={this.isLoading()}
              >
                <div className={styles.addButtonInner}>
                  <div className={styles.addTextWrapper}>
                    <span
                      className={
                        minOrderQty > 1 ? styles.addTextCompact : styles.addText
                      }
                    >
                      ADD
                    </span>
                    <span className={styles.addIcon}>+</span>
                  </div>
                  {minOrderQty > 1 && (
                    <span className={styles.minQtyInside}>
                      Min Qty {minOrderQty}
                    </span>
                  )}
                </div>
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  };

  renderOffers = () => {
    const { product, isAuthenticated, onSignInClick } = this.props;
    if (!product?.OfferV2?.Offers && !product?.SuperSaverSlab?.Text)
      return null;

    const { OfferV2: { Offers = [] } = {}, SuperSaverSlab = {} } = product;

    const hasOffers = Offers && Offers.length > 0;
    const hasSuperSaver = SuperSaverSlab?.Text;

    if (!hasOffers && !hasSuperSaver) return null;

    return (
      <div style={{ padding: '20px', background: "#fff", borderRadius: "0px 0px 18px 18px" }}>
      <div className={styles.offersContainer}>
        {/* Regular Offers */}
        {hasOffers &&
          Offers.map((offer, index) => {
            const offerQuantity = offer.CTA?.Action?.ActionData?.Quantity;
            const isOfferApplied = this.state.currentQuantity === offerQuantity && product?.BottomCTA;

            return (
              <div key={index}>
                {isOfferApplied ? (
                  <div className={styles.savingsStateContainer}>
                    <div className={styles.savingsStateContent}>
                      {product.BottomCTA.IconImage && (
                        <div className={styles.savingsIcon}>
                          <ZImage
                            src={product.BottomCTA.IconImage}
                            alt="Savings"
                            className={styles.savingsIconImage}
                          />
                        </div>
                      )}
                      <div className={styles.savingsText} style={{ color: product.BottomCTA.TextColor }}>
                        {product.BottomCTA.Text}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={styles.offerRow}>
                    <div className={styles.offerText} style={{ color: offer.TextColor }}>
                      {newLineText(offer.Text)}
                    </div>
                    <div className={styles.offerCtaContainer}>
                      <button
                        type="button"
                        className={`${styles.offerAddButton} ${this.isLoading() ? styles.loading : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isAuthenticated) {
                            onSignInClick && onSignInClick();
                          } else {
                            this.handleAddClick(
                              offer.CTA?.Action?.ActionData?.Quantity,
                              offer.CTA?.Action?.ActionData?.InputType,
                            );
                          }
                        }}
                        disabled={this.isLoading()}
                      >
                        {this.isLoading() ? (
                          <Spinner
                            inverse={false}
                            color="#e23744"
                            width="14px"
                          />
                        ) : (
                          offer.CTA?.Text
                        )}
                      </button>
                      {offer.ImagePath && (
                        <div className={styles.tickImageContainer}>
                          <ZImage
                            src={offer.ImagePath}
                            alt="Applied"
                            className={styles.tickImage}
                            width={20}
                            height={20}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {(index < Offers.length - 1 || hasSuperSaver) && (
                  <div className={styles.offerDivider} />
                )}
              </div>
            );
          })}

        {/* SuperSaver Slab */}
        {hasSuperSaver && (
          <>
            {SuperSaverSlab?.Tag && (
              <div className={styles.superSaverTagContainer}>
                <div
                  className={styles.superSaverTag}
                  style={{ backgroundColor: SuperSaverSlab.Tag?.BgColor }}
                >
                  {SuperSaverSlab.Tag?.ImagePath && (
                    <img
                      src={SuperSaverSlab.Tag.ImagePath}
                      alt="supersaver"
                      className={styles.superSaverIcon}
                    />
                  )}
                  <span
                    className={styles.superSaverTagText}
                    style={{ color: SuperSaverSlab.Tag?.TextColor }}
                  >
                    {SuperSaverSlab.Tag?.Text}
                  </span>
                </div>
              </div>
            )}
            {(() => {
              const superSaverQuantity = SuperSaverSlab.CTA?.Action?.ActionData?.Quantity;
              const isSuperSaverApplied = this.state.currentQuantity === superSaverQuantity && product?.BottomCTA;

              return isSuperSaverApplied ? (
                <div className={styles.savingsStateContainer}>
                  <div className={styles.savingsStateContent}>
                    {product.BottomCTA.IconImage && (
                      <div className={styles.savingsIcon}>
                        <ZImage
                          src={product.BottomCTA.IconImage}
                          alt="Savings"
                          className={styles.savingsIconImage}
                        />
                      </div>
                    )}
                    <div
                      className={styles.savingsText}
                      style={{ color: product.BottomCTA.TextColor }}
                    >
                      {product.BottomCTA.Text}
                    </div>
                  </div>
                </div>
              ) : (
                <div className={styles.offerRow}>
                  <div className={styles.offerText} style={{ color: SuperSaverSlab.TextColor }}>
                    {newLineText(SuperSaverSlab.Text)}
                  </div>
                  <div className={styles.offerCtaContainer}>
                    <button
                      type="button"
                      className={`${styles.offerAddButton} ${this.isLoading() ? styles.loading : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isAuthenticated) {
                          onSignInClick && onSignInClick();
                        } else {
                          this.handleAddClick(
                            SuperSaverSlab.CTA?.Action?.ActionData?.Quantity,
                            SuperSaverSlab.CTA?.Action?.ActionData?.InputType,
                          );
                        }
                      }}
                      disabled={this.isLoading()}
                    >
                      {this.isLoading() ? (
                        <Spinner
                          inverse={false}
                          color="#e23744"
                          width="14px"
                        />
                      ) : (
                        SuperSaverSlab.CTA?.Text
                      )}
                    </button>
                    {SuperSaverSlab.ImagePath && (
                      <div className={styles.tickImageContainer}>
                        <ZImage
                          src={SuperSaverSlab.ImagePath}
                          alt="Applied"
                          className={styles.tickImage}
                          width={20}
                          height={20}
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </>
        )}
      </div>
      </div>
    );
  };

  render() {
    const { visible, onClose } = this.props;

    return (
      <Modal
        visible={visible}
        onClose={onClose}
        title="Select quantity"
        titleFont="24"
        titleFontWeight="700"
        showCloseIcon={true}
        isCentered={true}
        slideOnMobile={true}
        overlayClickClose={true}
        borderRadius="24px"
        maxHeight="80vh"
        padding="16px"
        type="midScreenSize"
        bgColor="#F2F4F7"
        className={styles.productOffersModal}
        mobileHeight={'40vh'}
      >
        <div className={styles.modalContent}>
          {this.renderProductHeaderSection()}
          {this.renderOffers()}
        </div>
      </Modal>
    );
  }
}

export default ProductOffersModal;
